import { useState } from 'react'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import * as api from '../../db/api.js'
import { normalizeDomain } from '../../lib/url.js'
import './meu-perfil.css'
import './config-clientes.css'

/**
 * Clientes — tela dedicada da área de Configurações.
 *
 * Lista os clientes aos quais a organização do usuário está vinculada (nome,
 * URL) e permite editar nome/URL — mesma ação já existente. As integrações por
 * cliente (WhatsApp, WhatsApp Oficial, Tracker) NÃO ficam mais aqui: foram
 * movidas para a tela de Conexões. Nenhuma regra de negócio muda.
 */
export default function ConfigClientes() {
  return <ClientsSection />
}

/**
 * Gerenciamento dos clientes vinculados. Permite editar apenas o nome e a
 * URL do site (domain) de cada cliente — nenhum outro campo. As mudanças vão
 * para a tabela `clients` e o backend sincroniza `ad_accounts.domain`.
 *
 * Visível só para owner/admin (quem enxerga clientes via org_membership). O
 * backend reforça a permissão; aqui escondemos a edição para os demais.
 */
function ClientsSection() {
  const { availableClients, toast, refreshAccess, organizations, activeOrganizationId } = useApp()
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ name: '', domain: '' })
  const [saving, setSaving] = useState(false)

  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', domain: '' })
  const [createSaving, setCreateSaving] = useState(false)

  // Issue #94: papel na organização ATIVA, não em qualquer organização do
  // usuário — quem é owner numa org e viewer na outra só pode criar/editar
  // clients enquanto a org onde ele é owner/admin estiver ativa (backend
  // reforça isso via o header de organização ativa; aqui só escondemos a UI).
  const activeOrganization = (organizations || []).find(
    (o) => o.organization_id === activeOrganizationId
  )
  const activeRole = activeOrganization?.role
  const canEditClients = ['owner', 'admin'].includes(activeRole)
  const clients = availableClients || []

  function startCreate() {
    setEditingId(null)
    setCreating(true)
    setCreateForm({ name: '', domain: '' })
  }

  function cancelCreate() {
    setCreating(false)
    setCreateForm({ name: '', domain: '' })
  }

  // Client criado aqui é sempre "comum" (is_self=false, domínio obrigatório —
  // mesma regra do backend em POST /api/clients). O client "próprio" nasce
  // só no onboarding.
  async function handleCreate() {
    const name = createForm.name.trim()
    if (!name) {
      toast('Nome do cliente é obrigatório.', 'error')
      return
    }
    let domain
    try {
      domain = normalizeDomain(createForm.domain)
    } catch (err) {
      toast(err.message, 'error')
      return
    }
    if (!domain) {
      toast('URL do site do cliente é obrigatória.', 'error')
      return
    }
    try {
      setCreateSaving(true)
      // Organização de destino é a organização ativa da sessão (header
      // injetado em toda request — ver AppContext/api.js, issue #94); não
      // precisa ser escolhida aqui de novo.
      await api.createClient({ name, isSelf: false, domain })
      await refreshAccess?.()
      toast('Cliente criado.', 'success')
      cancelCreate()
    } catch (err) {
      toast(err.message || 'Falha ao criar cliente.', 'error')
    } finally {
      setCreateSaving(false)
    }
  }

  function startEdit(client) {
    setCreating(false)
    setEditingId(client.id)
    setForm({ name: client.name || '', domain: client.domain || '' })
  }

  function cancelEdit() {
    setEditingId(null)
    setForm({ name: '', domain: '' })
  }

  async function handleSave(client) {
    const name = form.name.trim()
    if (!name) {
      toast('Nome do cliente é obrigatório.', 'error')
      return
    }
    let domain
    try {
      domain = normalizeDomain(form.domain)
    } catch (err) {
      toast(err.message, 'error')
      return
    }
    // is_self pode não ter domínio; clients comuns exigem URL.
    if (!client.is_self && !domain) {
      toast('URL do site do cliente é obrigatória.', 'error')
      return
    }
    try {
      setSaving(true)
      await api.updateClient(client.id, { name, domain: domain || '' })
      await refreshAccess?.()
      toast('Cliente atualizado.', 'success')
      cancelEdit()
    } catch (err) {
      toast(err.message || 'Falha ao atualizar cliente.', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mp-page cl-settings-page">
      <header className="mp-card cl-settings-header">
        <div className="cl-settings-title">
          <span className="cl-settings-mark"><Icon n="briefcase" s={21} /></span>
          <div>
            <h1>Clientes vinculados <span className="cl-settings-count" aria-label={`${clients.length} clientes`}>{clients.length}</span></h1>
            <p>{activeOrganization?.organization_name || 'Organização atual'} · Gerencie o nome e o site dos seus clientes.</p>
          </div>
        </div>
        {canEditClients && !creating && <button type="button" className="mp-button mp-button-primary" onClick={startCreate}>
          <Icon n="plus" s={14} />Criar cliente
        </button>}
      </header>

      {canEditClients && creating && <section className="mp-card cl-settings-create" aria-labelledby="cl-create-title">
        <div className="mp-section-heading">
          <span className="mp-section-icon"><Icon n="plus" s={17} /></span>
          <div><h2 id="cl-create-title">Novo cliente</h2><p>Informe o nome e o site para adicionar o cliente.</p></div>
        </div>
        <ClientFields prefix="cl-create" values={createForm} onChange={setCreateForm} disabled={createSaving} autoFocus />
        <div className="cl-settings-actions">
          <button type="button" className="mp-button" onClick={cancelCreate} disabled={createSaving}>Cancelar</button>
          <button type="button" className="mp-button mp-button-primary" onClick={handleCreate} disabled={createSaving}>
            <Icon n="plus" s={14} />{createSaving ? 'Criando…' : 'Criar cliente'}
          </button>
        </div>
      </section>}

      {clients.length === 0 ? <div className="mp-card cl-settings-empty">
        <span className="mp-section-icon"><Icon n="briefcase" s={20} /></span>
        <h2>Nenhum cliente cadastrado ainda</h2>
        <p>{canEditClients ? 'Use “Criar cliente” para adicionar o primeiro.' : 'Os clientes disponíveis para seu acesso aparecerão aqui.'}</p>
      </div> : <ul className={`cl-settings-grid${editingId ? ' has-editor' : ''}`} aria-label="Clientes vinculados">
        {clients.map(client => {
          const isEditing = editingId === client.id
          const initials = ((client.name || 'Cliente').match(/[\p{L}\p{N}]+/gu) || ['C']).slice(0, 2).map(part => part[0]).join('').toUpperCase()
          return <li key={client.id} className={`mp-card cl-settings-card${isEditing ? ' is-editing' : ''}`}>
            <div className="cl-settings-card-head">
              <span className="cl-settings-avatar" aria-hidden="true">{initials}</span>
              <div className="cl-settings-client">
                <h2>{client.name}{client.is_self && <span className="cl-settings-self">Próprio</span>}</h2>
                <p><Icon n="meta" s={12} /><span>{client.domain || 'Sem URL definida'}</span></p>
              </div>
              {canEditClients && !isEditing && <button type="button" className="mp-button" onClick={() => startEdit(client)} aria-label={`Editar ${client.name}`}>
                <Icon n="settings" s={13} />Editar
              </button>}
            </div>
            {isEditing && <>
              <ClientFields prefix={`cl-edit-${client.id}`} values={form} onChange={setForm} disabled={saving} />
              <div className="cl-settings-actions">
                <button type="button" className="mp-button" onClick={cancelEdit} disabled={saving}>Cancelar</button>
                <button type="button" className="mp-button mp-button-primary" onClick={() => handleSave(client)} disabled={saving}>
                  <Icon n="check" s={14} />{saving ? 'Salvando…' : 'Salvar alterações'}
                </button>
              </div>
            </>}
          </li>
        })}
      </ul>}
    </div>
  )
}

function ClientFields({ prefix, values, onChange, disabled, autoFocus = false }) {
  return <div className="cl-settings-fields">
    <div className="mp-field">
      <label htmlFor={`${prefix}-name`}>Nome do cliente</label>
      <input id={`${prefix}-name`} value={values.name} onChange={event => onChange(previous => ({ ...previous, name: event.target.value }))} placeholder="Nome do cliente" disabled={disabled} autoFocus={autoFocus} />
    </div>
    <div className="mp-field">
      <label htmlFor={`${prefix}-domain`}>URL do site</label>
      <input id={`${prefix}-domain`} value={values.domain} onChange={event => onChange(previous => ({ ...previous, domain: event.target.value }))} placeholder="exemplo.com.br" disabled={disabled} autoCapitalize="none" spellCheck={false} />
    </div>
  </div>
}
